import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Button, TextField, Chip,
  FormControl, InputLabel, Select, MenuItem,
  TablePagination, IconButton,
  Tooltip, InputAdornment, Badge, Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Clear as ClearIcon,
  Description as DocIcon, AttachFile as AttachIcon, Print as PrintIcon,
  ShoppingCart as POIcon, Receipt as InvoiceIcon, AssignmentReturn as ReturnIcon,
  LocalShipping as IssueIcon, SwapHoriz as TransferIcon, Inventory as CountIcon,
  AccountBalance as VoucherIcon, MenuBook as JournalIcon, PointOfSale as SalesIcon,
  FilterAlt as FilterIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../services/axiosInstance';
import AttachmentSection from '../components/common/AttachmentSection';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';

// ============================================
interface ArchiveDocument {
  documentId: number; documentType: string; documentTypeAr: string;
  documentNumber: string; documentDate: string; status: string; totalAmount: number;
  branchId: number | null; branchName: string; branchNameAr: string;
  warehouseId: number | null; warehouseName: string; warehouseNameAr: string;
  supplierId: number | null; supplierName: string;
  custodyId: number | null; custodyDisplay: string;
  notes: string; createdBy: number | null; createdByName: string; createdDate: string;
}
interface ArchiveResult {
  documents: ArchiveDocument[]; totalCount: number; page: number; pageSize: number;
  totalPages: number; countByType: Record<string, number>; grandTotal: number;
}
interface Branch { branchId: number; branchName: string; branchNameAr: string; }
interface Supplier { supplierId: number; supplierCode: string; supplierName: string; supplierNameAr: string; }
interface Custody { custodyId: number; custodyNumber: string; employeeName: string; employeeNameAr: string; status: string; }
interface Warehouse { warehouseId: number; warehouseName: string; warehouseNameAr: string; }

const docTypeIcons: Record<string, React.ReactNode> = {
  PurchaseOrder: <POIcon fontSize="small" color="primary" />,
  PurchaseInvoice: <InvoiceIcon fontSize="small" color="secondary" />,
  PurchaseReturn: <ReturnIcon fontSize="small" color="warning" />,
  StockIssue: <IssueIcon fontSize="small" color="info" />,
  WarehouseTransfer: <TransferIcon fontSize="small" color="success" />,
  BranchTransfer: <TransferIcon fontSize="small" sx={{ color: '#9c27b0' }} />,
  StockCount: <CountIcon fontSize="small" color="action" />,
  Voucher: <VoucherIcon fontSize="small" sx={{ color: '#e91e63' }} />,
  JournalEntry: <JournalIcon fontSize="small" sx={{ color: '#ff9800' }} />,
  DailySale: <SalesIcon fontSize="small" sx={{ color: '#4caf50' }} />,
};

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  Approved: 'success', Posted: 'success', Completed: 'success',
  Pending: 'warning', Draft: 'warning', Open: 'warning',
  Cancelled: 'error', Rejected: 'error',
  Received: 'info', Shipped: 'info',
};

const translations = {
  en: {
    title: 'Transaction Log', subtitle: 'Unified view of all system documents',
    fromDate: 'From Date', toDate: 'To Date', documentType: 'Document Type', allTypes: 'All Types',
    branches: 'Branches', allBranches: 'All Branches', supplier: 'Supplier', allSuppliers: 'All Suppliers',
    custody: 'Custody', allCustodies: 'All Custodies', warehouse: 'Warehouse', allWarehouses: 'All Warehouses',
    status: 'Status', allStatuses: 'All', searchPlaceholder: 'Search number, notes, name...',
    search: 'Search', clear: 'Clear', print: 'Print', selectAll: 'Select All', searchInList: 'Search...',
    docNumber: 'Doc #', type: 'Type', date: 'Date', amount: 'Amount', branch: 'Branch',
    createdBy: 'Created By', notes: 'Notes', attachments: 'Attachments',
    noData: 'No documents found', total: 'Total', kwd: 'KWD',
    rowsPerPage: 'Rows per page',
    PurchaseOrder: 'Purchase Order', PurchaseInvoice: 'Purchase Invoice', PurchaseReturn: 'Purchase Return',
    StockIssue: 'Stock Issue', WarehouseTransfer: 'Warehouse Transfer', BranchTransfer: 'Branch Transfer',
    StockCount: 'Stock Count', Voucher: 'Voucher', JournalEntry: 'Journal Entry', DailySale: 'Daily Sale',
  },
  ar: {
    title: 'سجل المعاملات', subtitle: 'عرض موحّد لكل مستندات النظام',
    fromDate: 'من تاريخ', toDate: 'إلى تاريخ', documentType: 'نوع المستند', allTypes: 'كل الأنواع',
    branches: 'الفروع', allBranches: 'كل الفروع', supplier: 'المورد', allSuppliers: 'كل الموردين',
    custody: 'العهدة', allCustodies: 'كل العهد', warehouse: 'المخزن', allWarehouses: 'كل المخازن',
    status: 'الحالة', allStatuses: 'الكل', searchPlaceholder: 'بحث بالرقم، الملاحظات، الاسم...',
    search: 'بحث', clear: 'مسح', print: 'طباعة', selectAll: 'اختيار الكل', searchInList: 'بحث...',
    docNumber: 'رقم المستند', type: 'النوع', date: 'التاريخ', amount: 'المبلغ', branch: 'الفرع',
    createdBy: 'بواسطة', notes: 'ملاحظات', attachments: 'المرفقات',
    noData: 'لا توجد مستندات', total: 'الإجمالي', kwd: 'د.ك',
    rowsPerPage: 'صفوف في الصفحة',
    PurchaseOrder: 'أمر شراء', PurchaseInvoice: 'فاتورة شراء', PurchaseReturn: 'مرتجع شراء',
    StockIssue: 'إذن صرف', WarehouseTransfer: 'تحويل مخزني', BranchTransfer: 'تحويل فروع',
    StockCount: 'جرد', Voucher: 'سند', JournalEntry: 'قيد يومية', DailySale: 'مبيعات يومية',
  }
};

const docTypes = [
  'PurchaseOrder', 'PurchaseInvoice', 'PurchaseReturn', 'StockIssue',
  'WarehouseTransfer', 'BranchTransfer', 'StockCount', 'Voucher', 'JournalEntry', 'DailySale'
];
const statuses = ['Draft', 'Pending', 'Approved', 'Posted', 'Completed', 'Cancelled', 'Open', 'Received', 'Shipped'];

const DocumentArchivePage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const isRtl = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const getMonthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
  const getToday = () => new Date().toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(getMonthStart());
  const [toDate, setToDate] = useState(getToday());
  const [documentType, setDocumentType] = useState('');
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [warehouseId, setWarehouseId] = useState<number | ''>('');
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [custodyId, setCustodyId] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  const [data, setData] = useState<ArchiveResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [attachCounts, setAttachCounts] = useState<Record<string, number>>({});
  const [attachDoc, setAttachDoc] = useState<ArchiveDocument | null>(null);


  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [brRes, supRes, cusRes, whRes] = await Promise.allSettled([
          axiosInstance.get('/Branches'), axiosInstance.get('/Suppliers'),
          axiosInstance.get('/Custodies/lookup'), axiosInstance.get('/Warehouses'),
        ]);
        if (brRes.status === 'fulfilled') setBranches(brRes.value.data.filter?.((b: any) => b.isActive) || brRes.value.data);
        if (supRes.status === 'fulfilled') setSuppliers(supRes.value.data);
        if (cusRes.status === 'fulfilled') setCustodies(cusRes.value.data.filter?.((c: any) => c.status === 'Open') || cusRes.value.data);
        if (whRes.status === 'fulfilled') setWarehouses(whRes.value.data.filter?.((w: any) => w.isActive !== false) || whRes.value.data);
      } catch { console.error('Failed to fetch lookups'); }
    };
    fetchLookups();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = `fromDate=${fromDate}&toDate=${toDate}&page=${page + 1}&pageSize=${pageSize}`;
      if (documentType) params += `&documentType=${documentType}`;
      if (selectedBranches.length > 0) params += `&branchIds=${selectedBranches.join(',')}`;
      if (warehouseId) params += `&warehouseId=${warehouseId}`;
      if (supplierId) params += `&supplierId=${supplierId}`;
      if (custodyId) params += `&custodyId=${custodyId}`;
      if (statusFilter) params += `&status=${statusFilter}`;
      if (searchText.trim()) params += `&search=${encodeURIComponent(searchText.trim())}`;
      const response = await axiosInstance.get(`/Archive?${params}`);
      setData(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error');
    } finally { setLoading(false); }
  }, [fromDate, toDate, documentType, selectedBranches, warehouseId, supplierId, custodyId, statusFilter, searchText, page, pageSize]);

  useEffect(() => { fetchData(); }, [page, pageSize]);

  // ✅ جلب عدد المرفقات لكل مستند معروض
  useEffect(() => {
    if (!data || data.documents.length === 0) return;
    const fetchCounts = async () => {
      const counts: Record<string, number> = {};
      const promises = data.documents.map(async (doc) => {
        const key = `${doc.documentType}-${doc.documentId}`;
        try {
          const res = await axiosInstance.get(`/DocumentAttachments/count/${doc.documentType}/${doc.documentId}`);
          counts[key] = typeof res.data === 'number' ? res.data : (res.data?.count || 0);
        } catch { counts[key] = 0; }
      });
      await Promise.all(promises);
      setAttachCounts(counts);
    };
    fetchCounts();
  }, [data]);


  const clearFilters = () => {
    setDocumentType(''); setSelectedBranches([]); setWarehouseId('');
    setSupplierId(''); setCustodyId(''); setStatusFilter(''); setSearchText('');
  };

  const handlePrint = () => {
    if (!data || data.documents.length === 0) return;
    const pw = window.open('', '_blank'); if (!pw) return;
    const rows = data.documents.map(doc =>
      `<tr><td>${doc.documentNumber}</td><td>${isRtl ? doc.documentTypeAr : doc.documentType}</td><td>${doc.documentDate}</td><td>${doc.status}</td><td style="text-align:right;">${doc.totalAmount.toFixed(3)}</td><td>${isRtl ? doc.branchNameAr : doc.branchName}</td><td>${doc.createdByName}</td><td>${doc.notes || ''}</td></tr>`
    ).join('');
    pw.document.write(`<!DOCTYPE html><html dir="${isRtl ? 'rtl' : 'ltr'}"><head><title>${t.title}</title>
      <style>body{font-family:Arial;margin:20px;font-size:11px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:5px;}th{background:#333;color:#fff;}@media print{body{margin:10px;}}</style></head><body>
      <h2>${t.title}</h2><p>${t.fromDate}: ${data.documents[0]?.documentDate || fromDate} — ${t.toDate}: ${toDate}</p>
      <table><thead><tr><th>${t.docNumber}</th><th>${t.type}</th><th>${t.date}</th><th>${t.status}</th><th>${t.amount}</th><th>${t.branch}</th><th>${t.createdBy}</th><th>${t.notes}</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    pw.document.close(); pw.print();
  };

  // تحديث عدّاد مرفقات مستند واحد بعد رفع/حذف داخل النافذة الداخلية
  const refreshOneCount = async (doc: ArchiveDocument) => {
    const key = `${doc.documentType}-${doc.documentId}`;
    try {
      const res = await axiosInstance.get(`/DocumentAttachments/count/${doc.documentType}/${doc.documentId}`);
      const c = typeof res.data === 'number' ? res.data : (res.data?.count || 0);
      setAttachCounts(prev => ({ ...prev, [key]: c }));
    } catch { /* تجاهل */ }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRtl ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <DocIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
          <Typography variant="body2" color="text.secondary">{t.subtitle}</Typography>
        </Box>
      </Box>

      {/* ========== الفلاتر ========== */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.fromDate}
            value={fromDate ? new Date(fromDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setFromDate(newVal);
              if (toDate && newVal && toDate < newVal) setToDate('');
            }}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={toDate ? new Date(toDate) : maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', fullWidth: true, sx: { minWidth: 150 } } }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.toDate}
            value={toDate ? new Date(toDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setToDate(newVal);
              if (fromDate && newVal && fromDate > newVal) setFromDate('');
            }}
            format="dd/MM/yyyy"
            minDate={fromDate ? new Date(fromDate) : minDate}
            maxDate={maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', fullWidth: true, sx: { minWidth: 150 } } }}
          />
        </LocalizationProvider>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t.documentType}</InputLabel>
          <Select value={documentType} onChange={e => setDocumentType(e.target.value)} label={t.documentType}>
            <MenuItem value="">{t.allTypes}</MenuItem>
            {docTypes.map(dt => <MenuItem key={dt} value={dt}>{docTypeIcons[dt]} <Box component="span" sx={{ mx: 0.5 }}>{(t as any)[dt] || dt}</Box></MenuItem>)}
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          limitTags={2}
          size="small"
          sx={{ minWidth: 200 }}
          options={branches}
          getOptionLabel={(b) => isRtl ? b.branchNameAr : b.branchName}
          value={branches.filter(b => selectedBranches.includes(b.branchId))}
          onChange={(_, v) => setSelectedBranches(v.map(b => b.branchId))}
          isOptionEqualToValue={(o, v) => o.branchId === v.branchId}
          renderInput={(params) => (
            <TextField {...params} label={t.branches} placeholder={t.allBranches} />
          )}
        />

        <Autocomplete
          size="small"
          sx={{ minWidth: 180 }}
          options={warehouses}
          getOptionLabel={(w) => isRtl ? (w.warehouseNameAr || w.warehouseName) : w.warehouseName}
          value={warehouses.find(w => w.warehouseId === warehouseId) || null}
          onChange={(_, v) => setWarehouseId(v ? v.warehouseId : '')}
          isOptionEqualToValue={(o, v) => o.warehouseId === v.warehouseId}
          renderInput={(params) => (
            <TextField {...params} label={t.warehouse} placeholder={t.allWarehouses} />
          )}
        />

        <Autocomplete
          size="small"
          sx={{ minWidth: 200 }}
          options={suppliers}
          getOptionLabel={(s) => `${s.supplierCode} - ${isRtl ? (s.supplierNameAr || s.supplierName) : s.supplierName}`}
          value={suppliers.find(s => s.supplierId === supplierId) || null}
          onChange={(_, v) => setSupplierId(v ? v.supplierId : '')}
          isOptionEqualToValue={(o, v) => o.supplierId === v.supplierId}
          renderInput={(params) => (
            <TextField {...params} label={t.supplier} placeholder={t.allSuppliers} />
          )}
        />

        <Autocomplete
          size="small"
          sx={{ minWidth: 220 }}
          options={custodies}
          getOptionLabel={(c) => `${c.custodyNumber} - ${isRtl ? (c.employeeNameAr || c.employeeName) : c.employeeName}`}
          value={custodies.find(c => c.custodyId === custodyId) || null}
          onChange={(_, v) => setCustodyId(v ? v.custodyId : '')}
          isOptionEqualToValue={(o, v) => o.custodyId === v.custodyId}
          renderInput={(params) => (
            <TextField {...params} label={t.custody} placeholder={t.allCustodies} />
          )}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>{t.status}</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label={t.status}>
            <MenuItem value="">{t.allStatuses}</MenuItem>
            {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField size="small" placeholder={t.searchPlaceholder} value={searchText} onChange={e => setSearchText(e.target.value)}
          sx={{ minWidth: 200 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

        <Button variant="contained" startIcon={<SearchIcon />} onClick={() => { setPage(0); fetchData(); }} disabled={loading}>{t.search}</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} size="small">{t.clear}</Button>
        {data && data.documents.length > 0 && (
          <Button variant="outlined" color="secondary" startIcon={<PrintIcon />} onClick={handlePrint}>{t.print}</Button>
        )}
      </Paper>

      {data && data.totalCount > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${t.total}: ${data.totalCount}`} color="primary" size="small" />
          <Chip label={`${data.grandTotal.toFixed(3)} ${t.kwd}`} color="success" size="small" />
          {Object.entries(data.countByType).map(([type, count]) => (
            <Chip key={type} icon={<span>{docTypeIcons[type]}</span>} label={`${(t as any)[type] || type}: ${count}`} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : data && data.documents.length > 0 ? (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' } }}>
                  <TableCell width={40} /><TableCell>{t.docNumber}</TableCell><TableCell>{t.type}</TableCell>
                  <TableCell>{t.date}</TableCell><TableCell>{t.status}</TableCell><TableCell align="right">{t.amount}</TableCell>
                  <TableCell>{t.branch}</TableCell><TableCell>{t.createdBy}</TableCell>
                  <TableCell>{t.notes}</TableCell><TableCell align="center">{t.attachments}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.documents.map((doc, idx) => (
                  <TableRow key={`${doc.documentType}-${doc.documentId}-${idx}`} hover>
                    <TableCell>{docTypeIcons[doc.documentType] || <DocIcon fontSize="small" />}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{doc.documentNumber}</TableCell>
                    <TableCell><Chip label={isRtl ? doc.documentTypeAr : (t as any)[doc.documentType] || doc.documentType} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{doc.documentDate}</TableCell>
                    <TableCell><Chip label={doc.status} size="small" color={statusColors[doc.status] || 'default'} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: doc.totalAmount > 0 ? 'bold' : 'normal' }}>
                      {doc.totalAmount > 0 ? `${doc.totalAmount.toFixed(3)}` : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{isRtl ? doc.branchNameAr : doc.branchName}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{doc.createdByName}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={doc.notes || ''}><span>{doc.notes || '-'}</span></Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const key = `${doc.documentType}-${doc.documentId}`;
                        const count = attachCounts[key] || 0;
                        return (
                          <Tooltip title={count > 0 ? `${t.attachments} (${count})` : t.attachments}>
                            <IconButton size="small" onClick={() => setAttachDoc(doc)}
                              sx={{ color: count > 0 ? 'success.main' : 'primary.main' }}>
                              <Badge badgeContent={count > 0 ? count : undefined} color="success" max={99}
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
                                <AttachIcon fontSize="small" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={data.totalCount} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize} onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage={t.rowsPerPage} />
        </Paper>
      ) : data ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">{t.noData}</Typography></Paper>
      ) : null}

      {attachDoc && (
        <AttachmentSection
          key={`${attachDoc.documentType}-${attachDoc.documentId}`}
          documentType={attachDoc.documentType}
          documentId={attachDoc.documentId}
          isRTL={isRtl}
          hideTrigger
          open={!!attachDoc}
          onClose={() => setAttachDoc(null)}
          branchId={attachDoc.branchId}
          warehouseId={attachDoc.warehouseId}
          custodyId={attachDoc.custodyId}
          titleSuffix={attachDoc.documentNumber}
          onChanged={() => refreshOneCount(attachDoc)}
        />
      )}
    </Box>
  );
};

export default DocumentArchivePage;